import { AnonymousGuard } from './services/anonymous-guard.service'
import { AuthGuard } from './services/auth.guard.service'
import { Routes, RouterModule } from '@angular/router'
import { NgModule } from '@angular/core'
import { AccessControlGuard } from './services/access-control-guard.service'
import { TabHubComponent } from './core/navigation/tab-hub/tab-hub.component'

const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: '/dashboard' },
  {
    path: 'auth',
    loadChildren: () => import('./pages/auth/auth.module').then((m) => m.AuthModule),
    canActivate: [AnonymousGuard],
    data: { requiredPage: 'auth' },
  },
  {
    /**
     * AuthGuard only, and deliberately no `requiredPage`. This is where every admin
     * lands, and no one has a `dashboard` row in the web.pages registry — gating it
     * would greet most of the team with a blank screen. Each block on the page
     * checks its own permission instead.
     */
    path: 'dashboard',
    loadChildren: () => import('./pages/dashboard/dashboard.module').then((m) => m.DashboardModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'requests',
    loadChildren: () => import('./pages/requests/requests.module').then((m) => m.RequestsModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'requests' },
  },
  // { path: 'photos', loadChildren: () => import('./pages/photos/photos.module').then(m => m.PhotosModule), canActivate: [AuthGuard, AccessControlGuard], data: { requiredPage: 'photos' } },
  {
    path: 'users',
    loadChildren: () => import('./pages/users/users.module').then((m) => m.UsersModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'users' },
  },
  {
    path: 'events',
    loadChildren: () => import('./pages/events/events.module').then((m) => m.EventsModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'events' },
  },
  {
    path: 'categories',
    loadChildren: () =>
      import('./pages/categories/categories.module').then((m) => m.CategoriesModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'categories' },
  },
  {
    path: 'geo',
    component: TabHubComponent,
    canActivate: [AuthGuard],
    data: { hubId: 'geo-group', title: 'البيانات الجغرافية' },
    children: [
      { path: '', pathMatch: 'full', children: [] },
      {
        path: 'cities',
        loadChildren: () => import('./pages/cities/cities.module').then((m) => m.CitiesModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'cities' },
      },
      {
        path: 'provinces',
        loadChildren: () =>
          import('./pages/provinces/provinces.module').then((m) => m.ProvincesModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'provinces' },
      },
      {
        path: 'locations',
        loadChildren: () =>
          import('./pages/locations/locations.module').then((m) => m.LocationsModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'locations' },
      },
    ],
  },
  {
    path: 'ticket-providers',
    loadChildren: () =>
      import('./pages/ticket-providers/ticket-providers.module').then(
        (m) => m.TicketProvidersModule,
      ),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'ticket-providers' },
  },
  {
    path: 'orders',
    loadChildren: () => import('./pages/orders/orders.module').then((m) => m.OrdersModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'orders' },
  },
  {
    path: 'tickets',
    loadChildren: () => import('./pages/tickets/tickets.module').then((m) => m.TicketsModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'tickets' },
  },
  {
    path: 'events-sections',
    loadChildren: () =>
      import('./pages/events-sections/events-sections.module').then((m) => m.EventsSectionsModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'events-sections' },
  },
  {
    path: 'banners',
    loadChildren: () => import('./pages/banners/banners.module').then((m) => m.BannersModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'banners' },
  },
  {
    path: 'informative-icons',
    loadChildren: () =>
      import('./pages/informative-icon/informative-icon.module').then(
        (m) => m.InformativeIconModule,
      ),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'informative-icons' },
  },
  {
    path: 'entertainment',
    loadChildren: () =>
      import('./pages/entertainment/entertainment.module').then((m) => m.EntertainmentModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'entertainment' },
  },
  {
    path: 'entertainment-alhokair',
    loadChildren: () =>
      import('./pages/alhokair-entertainment/alhokair-entertainment.module').then(
        (m) => m.AlhokairEntertainmentModule,
      ),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'entertainment-alhokair' },
  },
  {
    path: 'entertainment-purchased-tickets',
    loadChildren: () =>
      import('./pages/entertainment-purchased-tickets/entertainment-purchased-tickets.module').then(
        (m) => m.PurchasedTicketsEntertainmentModule,
      ),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'entertainment-purchased-tickets' },
  },
  {
    path: 'notifications',
    component: TabHubComponent,
    canActivate: [AuthGuard],
    data: { hubId: 'notifications-group', title: 'الإشعارات' },
    children: [
      // Matched so the bare hub path resolves at all; TabHubComponent then forwards
      // to the first tab this admin may open.
      { path: '', pathMatch: 'full', children: [] },
      {
        path: 'list',
        loadChildren: () =>
          import('./pages/notification/notification.module').then((m) => m.NotificationModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'notification' },
      },
      {
        path: 'categories',
        loadChildren: () =>
          import('./pages/notification-categories/notification-categories.module').then(
            (m) => m.NotificationCategoriesModule,
          ),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'notification-categories' },
      },
    ],
  },
  {
    path: 'almosafer',
    loadChildren: () => import('./pages/almosafer/almosafer.module').then((m) => m.AlmosaferModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'almosafer' },
  },
  {
    path: 'settings',
    component: TabHubComponent,
    canActivate: [AuthGuard],
    data: { hubId: 'settings-group', title: 'الاعدادات' },
    children: [
      { path: '', pathMatch: 'full', children: [] },
      {
        path: 'service-fees',
        loadChildren: () =>
          import('./pages/service-fees/service-fees.module').then((m) => m.ServiceFeesModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'service-fees' },
      },
      {
        path: 'web-pages',
        loadChildren: () =>
          import('./pages/web-pages/web-pages.module').then((m) => m.WebPagesModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'web-pages' },
      },
      {
        path: 'access-control',
        loadChildren: () =>
          import('./pages/access-control/access-control.module').then((m) => m.AccessControlModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'access-control' },
      },
      {
        path: 'admins',
        loadChildren: () => import('./pages/admins/admins.module').then((m) => m.AdminsModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'admins' },
      },
    ],
  },
  {
    path: 'coupons',
    loadChildren: () => import('./pages/coupons/coupons.module').then((m) => m.CouponsModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'coupons' },
  },
  {
    path: 'spin-wheel',
    loadChildren: () =>
      import('./pages/spin-wheel/spin-wheel.module').then((m) => m.SpinWheelModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'spin-wheel' },
  },
  {
    path: 'games',
    loadChildren: () => import('./pages/games/games.module').then((m) => m.GamesModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'games' },
  },
  {
    path: 'tiers',
    loadChildren: () => import('./pages/tiers/tiers.module').then((m) => m.TiersModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'tiers' },
  },
  {
    path: 'wallets',
    loadChildren: () => import('./pages/wallets/wallets.module').then((m) => m.WalletsModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'wallets' },
  },
  {
    path: 'support-tickets',
    loadChildren: () =>
      import('./pages/support-tickets/support-tickets.module').then((m) => m.SupportTicketsModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'support-tickets' },
  },
  {
    path: 'image-uploader',
    loadChildren: () =>
      import('./pages/image-uploader/image-uploader.module').then((m) => m.ImageUploaderModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'image-uploader' },
  },
  {
    path: 'payment-links',
    loadChildren: () =>
      import('./pages/payment-links/payment-links.module').then((m) => m.PaymentLinksModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'payment-links' },
  },
  {
    path: 'marketing',
    component: TabHubComponent,
    canActivate: [AuthGuard],
    data: { hubId: 'marketing-group', title: 'التسويق' },
    children: [
      { path: '', pathMatch: 'full', children: [] },
      {
        path: 'team',
        loadChildren: () =>
          import('./pages/marketing-team/marketing-team.module').then((m) => m.MarketingTeamModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'marketing-team' },
      },
      {
        path: 'agency',
        loadChildren: () =>
          import('./pages/marketing-agency/marketing-agency.module').then(
            (m) => m.MarketingAgencyModule,
          ),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'marketing-agency' },
      },
      {
        path: 'newsletter',
        loadChildren: () =>
          import('./pages/newsletter/newsletter.module').then((m) => m.NewsletterModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'newsletter' },
      },
    ],
  },
  {
    /**
     * Same shape as the `marketing` hub, with one difference: the five tabs live in
     * a single lazy module, so the hub mounts it once at `path: ''` and the module's
     * own routing owns `overview` / `suggestions` / `runs` / `reports` / `settings`.
     * The empty-path `pathMatch: 'full'` entry above it still resolves the bare
     * `/seo` URL, which is what `TabHubComponent` needs to forward to the first tab.
     */
    path: 'seo',
    component: TabHubComponent,
    canActivate: [AuthGuard],
    data: { hubId: 'seo-group', title: 'تحسين محركات البحث' },
    children: [
      { path: '', pathMatch: 'full', children: [] },
      {
        path: '',
        loadChildren: () => import('./pages/seo/seo.module').then((m) => m.SeoModule),
        canActivate: [AccessControlGuard],
        data: { requiredPage: 'seo' },
      },
    ],
  },
  {
    path: 'revenue-report',
    loadChildren: () =>
      import('./pages/revenue-report/revenue-report.module').then((m) => m.RevenueReportModule),
    canActivate: [AuthGuard, AccessControlGuard],
    data: { requiredPage: 'revenue-report' },
  },
  /**
   * Pages that moved into a hub keep their original top-level path as a redirect.
   *
   * Two reasons, both load-bearing. Links and bookmarks an admin already has keep
   * working — redirects match by prefix, so the rest of the path is carried across
   * and `/cities/update/42` lands on `/geo/cities/update/42`. And the access-control
   * screen builds its page-name dropdown from `getRoutes()` below, so these entries
   * are what keep every existing RBAC key selectable.
   */
  { path: 'cities', redirectTo: 'geo/cities' },
  { path: 'provinces', redirectTo: 'geo/provinces' },
  { path: 'locations', redirectTo: 'geo/locations' },
  { path: 'notification', redirectTo: 'notifications/list' },
  { path: 'notification-categories', redirectTo: 'notifications/categories' },
  { path: 'marketing-team', redirectTo: 'marketing/team' },
  { path: 'marketing-agency', redirectTo: 'marketing/agency' },
  { path: 'newsletter', redirectTo: 'marketing/newsletter' },
  { path: 'service-fees', redirectTo: 'settings/service-fees' },
  { path: 'web-pages', redirectTo: 'settings/web-pages' },
  { path: 'access-control', redirectTo: 'settings/access-control' },
  { path: 'admins', redirectTo: 'settings/admins' },
]

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {
  getRoutes() {
    return routes
  }
}
